<script setup>
import { offset, useFloating } from '@floating-ui/vue'
import { format } from '@formkit/tempo'
import { useAuthStore } from '@stores/auth'
import { onMounted, ref } from 'vue'

import Button from '@components/global/button.vue'
import Input from '@components/global/input.vue'

const auth = useAuthStore()
const processing = ref(false)
const loading2FA = ref(false)
const processing2FA = ref(false)
const details2FA = ref({})
const code2FA = ref('')
const account = ref({})
const errors = ref({})
//
const currentCodeRef = ref(null) // This will be set dynamically
const popover = ref(null)
const showPopover = ref(false)
const fadeOut = ref(false)
let timeoutId = null
const codeRefs = ref([])
function codeRef(idx) {
	return (el) => {
		if (el) {
			codeRefs.value[idx] = el
		} else {
			codeRefs.value[idx] = null
		}
	}
}
const { floatingStyles, update } = useFloating(currentCodeRef, popover, {
	placement: 'top',
	middleware: [offset(5)],
})

function prefilAccount() {
	account.value.email = auth.user?.email
	account.value.phone = auth.user?.phone
	account.value.birthday = auth.user?.birthday ? format(auth.user.birthday, 'YYYY-MM-DD', 'en') : null
}

function submit(form) {
	form.preventDefault()
	if (!processing.value) {
		processing.value = true
		auth.updateAccount(account.value)
			.then(() => {
				errors.value = {}
				processing.value = false
				prefilAccount()
			})
			.catch((errorBag) => {
				processing.value = false
				errors.value = errorBag
			})
	}
}

// 2FA
function setup2FA() {
	loading2FA.value = true
	auth.setup2FA().then((details) => {
		details2FA.value = details
		loading2FA.value = false
	})
}

function enable2FA() {
	processing2FA.value = true
	auth.enable2FA().then(() => {
		processing2FA.value = false
		setup2FA()
	})
}

function disable2FA() {
	processing2FA.value = true
	auth.disable2FA().then(() => {
		processing2FA.value = false
	})
}

function confirm2FA() {
	processing2FA.value = true
	auth.confirm2FA(code2FA.value)
		.then(() => {
			processing2FA.value = false
		})
		.catch((errorBag) => {
			processing2FA.value = false
			errors.value = errorBag
		})
}

async function copyCode(id, code) {
	currentCodeRef.value = codeRefs.value[id]
	try {
		await navigator.clipboard.writeText(code)
	} catch (e) {
		// fallback or error handling if needed
	}
	showPopover.value = true
	fadeOut.value = false
	update()
	if (timeoutId) clearTimeout(timeoutId)
	timeoutId = setTimeout(() => {
		fadeOut.value = true
		timeoutId = setTimeout(() => {
			showPopover.value = false
			fadeOut.value = false
			timeoutId = null
		}, 200)
	}, 1000)
}

onMounted(() => {
	prefilAccount()
	if (auth.user?.two_factor_enabled == true) {
		setup2FA()
	}
})
</script>

<template>
	<form class="flex w-full flex-col gap-8 rounded-max border border-border-default px-8 py-6 text-default" action="" @submit="submit">
		<h4 class="uppercase">Account Details</h4>
		<div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
			<Input class="lg:col-span-2" placeholder="Insert your email" label="email" type="email" :required="true" v-model="account.email" :error="errors.email && errors.email[0]" />
			<Input placeholder="Insert phone" label="phone number" max="13" filter="phone" v-model="account.phone" :error="errors.phone && errors.phone[0]" />
			<Input placeholder="DD-MM-YYYY" label="birthday" type="date" v-model="account.birthday" :error="errors.birthday && errors.birthday[0]" />
			<Input placeholder="**********" label="Password" type="password" v-model="account.password" :error="errors.password && errors.password[0]" />
			<Input placeholder="**********" label="Confirm Password" type="password" v-model="account.password_confirmation" :error="errors.password_confirmation && errors.password_confirmation[0]" />
		</div>
		<Button class="default w-full" :processing="processing">Save Changes</Button>
	</form>
	<!-- 2FA Settings -->
	<div class="flex w-full flex-col gap-6 rounded-max border border-border-default px-8 py-6 text-default">
		<h4 class="uppercase">2FA Settings</h4>
		<div class="flex h-32 flex-col items-center justify-center" v-if="loading2FA">
			<div class="relative flex animate-pulse items-center justify-center rounded-full">
				<i class="fa-thin fa-spinner-third animate-spin text-2xl font-bold text-white/50"></i>
			</div>
		</div>
		<template v-else-if="auth.user?.two_factor_enabled == false">
			<div class="label-medium grid grid-cols-1 gap-4 text-sm opacity-50">You currently don't have 2FA enabled.</div>
			<Button class="default w-full" :processing="processing2FA" @click="enable2FA">Begin Setup</Button>
		</template>
		<template v-else-if="auth.user?.two_factor_confirmed == false">
			<div class="flex flex-col gap-4">
				<div class="flex flex-col gap-4 lg:flex-row">
					<div class="w-full" v-html="details2FA?.qr"></div>
					<div class="flex w-full flex-col justify-between">
						<div class="mb-8 flex flex-col items-start gap-2">
							<p class="label-medium text-sm opacity-50">Please scan the QR code or enter the code manually:</p>
							<button class="rounded-[8px] border border-border-default bg-neutral-700 p-2 font-proto tracking-wider" :ref="codeRef(-1)" @click="copyCode(-1, details2FA.secretKey)">{{ details2FA.secretKey }}</button>
						</div>
						<div class="flex flex-col items-end gap-4">
							<Input id="totp" name="totp" inputmode="numeric" label="Authentication Code" placeholder="Enter your 6-digit code" max="6" v-model="code2FA" autocomplete="one-time-code" :error="errors.code && errors.code[0]" />
							<Button class="default" @click="confirm2FA" :processing="processing2FA" :disabled="code2FA.length !== 6">Complete Setup</Button>
						</div>
					</div>
				</div>
				<div class="col-span-2 flex flex-col items-start gap-2">
					<p class="label-medium text-sm text-[#77756f]">
						Please save these recovery codes for future use -
						<span class="text-red-light">they will not be shown after you complete setup!</span>
					</p>
					<div class="grid w-full grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
						<button class="w-full rounded-big border border-border-default bg-neutral-700 p-2 font-proto tracking-wider" v-for="(code, id) in details2FA.recoveryCodes" :key="code" :ref="codeRef(id)" @click="copyCode(id, code)">
							{{ code }}
						</button>
					</div>
				</div>
			</div>
		</template>
		<template v-else>
			<div class="label-medium grid grid-cols-1 gap-4 text-sm opacity-50">Your account is protected by 2FA.</div>
			<Button class="default" @click="disable2FA" :processing="processing2FA">Disable 2FA</Button>
		</template>
	</div>
	<div v-if="showPopover" ref="popover" :style="floatingStyles" :class="['rounded-md bg-neutral-500 px-2 py-1 font-proto text-sm tracking-wider text-white transition-opacity duration-500', { 'opacity-0': fadeOut }]">Copied to Clipboard</div>
</template>
